import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/common/Icon';
import AuthShell from './AuthShell';

// Landing page for the "confirm your new email" link. The signed token was
// stashed into sessionStorage by usePersistUrlParams (?email_change_token=…);
// we POST it to apply the change, then send the user to their profile (same
// device, still logged in) — the JWT is keyed on the user id, so it survives
// the email/username swap.
const ConfirmEmailChange = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const confirm = async () => {
      const token = sessionStorage.getItem('email_change_token');
      if (!token) {
        setError('invalidChangeToken');
        return;
      }
      try {
        const response = await fetch('/api/confirm-email-change', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        if (response.ok) {
          const data = await response.json();
          sessionStorage.removeItem('email_change_token');
          setMessage(data.message);
          setTimeout(() => navigate('/profile'), 4000);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'somethingWentWrong');
        }
      } catch (err) {
        setError(err.message);
      }
    };

    confirm();
  }, [location, navigate]);

  const status = error ? 'error' : message ? 'success' : 'pending';
  const iconName = status === 'success' ? 'check' : status === 'error' ? 'cross' : 'clock';
  const heroSubtitle =
    status === 'success'
      ? t('redirectingToProfile', { defaultValue: 'Taking you to your profile…' })
      : status === 'error'
      ? null
      : t('pleaseWait');

  return (
    <AuthShell
      title={t('confirmEmailChange')}
      subtitle={heroSubtitle}
      footer={
        status !== 'pending' && (
          <Link to="/login" className="auth__back-link">
            <Icon name="angle-left" /> {t('backToLogin')}
          </Link>
        )
      }
    >
      <div className="auth__card auth__verify">
        <div className={`auth__verify-icon auth__verify-icon--${status}`}>
          <Icon name={iconName} />
        </div>
        <p className="auth__verify-msg">
          {status === 'pending' && t('pleaseWait')}
          {message && t(message)}
          {error && t(error)}
        </p>
      </div>
    </AuthShell>
  );
};

export default ConfirmEmailChange;
